import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HelpDrawer from './HelpDrawer.jsx';
import { getHelpEntry } from './helpRegistry.js';

const HELP_TAB_STORAGE_KEY = 'sinamon-help-active-tab';

const HelpContext = createContext(null);

function readInitialTab() {
  if (typeof window === 'undefined') {
    return 'guide';
  }

  try {
    const stored = window.localStorage.getItem(HELP_TAB_STORAGE_KEY);
    return stored === 'assistant' ? 'assistant' : 'guide';
  } catch {
    return 'guide';
  }
}

export function HelpProvider({ children }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(readInitialTab);
  const helpEntry = useMemo(() => getHelpEntry(location.pathname), [location.pathname]);

  useEffect(() => {
    try {
      window.localStorage.setItem(HELP_TAB_STORAGE_KEY, activeTab);
    } catch {
      // La preferencia es opcional; si falla localStorage la ayuda sigue funcionando.
    }
  }, [activeTab]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'F1') {
        event.preventDefault();
        setIsOpen(true);
        return;
      }

      if (event.key === 'Escape') {
        setIsOpen((prev) => {
          if (!prev) return prev;
          event.preventDefault();
          return false;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const value = useMemo(() => ({
    helpEntry,
    isOpen,
    activeTab,
    openHelp: () => setIsOpen(true),
    closeHelp: () => setIsOpen(false),
    toggleHelp: () => setIsOpen((prev) => !prev),
    setActiveTab,
  }), [activeTab, helpEntry, isOpen]);

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpDrawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        entry={helpEntry}
      />
    </HelpContext.Provider>
  );
}

export function useHelp() {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelp debe usarse dentro de HelpProvider');
  }
  return context;
}

export default HelpProvider;
